import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { clearCartDeliveryAttributes, refreshCartDelivery } from '@theme/dev-cart-delivery';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Custom bundle groups are not in cartItemRows[] (no ref on the grouped row).
    // Without this check, removing the last classic line while a bundle remains flashes empty state.
    const hasBundleGroups = Boolean(this.querySelector('[data-dev-cart-bundle]'));

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length && !hasBundleGroups;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      clearCartDeliveryAttributes();

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        const drawerInner = this.isDrawer
          ? this.closest('[data-hydration-key="cart-drawer-inner"]')
          : null;

        const sectionHtml = await this.#resolveSectionHtml(parsedResponseText.sections[this.sectionId]);
        if (sectionHtml) this.#morphCartSection(sectionHtml, drawerInner);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const drawerInner = this.isDrawer
        ? this.closest('[data-hydration-key="cart-drawer-inner"]')
        : null;

      const sectionHtml = await this.#resolveSectionHtml(cartItemsHtml);
      if (sectionHtml) this.#morphCartSection(sectionHtml, drawerInner);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Morph cart section HTML (translated languages get server-translated HTML via `#resolveSectionHtml`).
   * Do not lock pointer-events on the whole drawer — that blocks mobile scroll and can freeze the UI.
   * In-flight clicks are handled by `cart-items-disabled` + `NoomaCartDrawerUpsellBusy`.
   * @param {string} sectionHtml
   * @param {Element | null} drawerInner - only set when `this.isDrawer` (never query header drawer from `/cart`)
   */
  #morphCartSection(sectionHtml, drawerInner) {
    const upsellBefore = this.#upsellSlideSignature(drawerInner);

    try {
      morphSection(this.sectionId, sectionHtml, this.isDrawer ? 'hydration' : 'full');
      this.#updateCartQuantitySelectorButtonStates();
      this.#enableCartItems();
      this.#syncUpsellAfterMorph(drawerInner, upsellBefore);
      // Cart morph injects EN Liquid into `.no-translate-wg` delivery — paint after DOM settles.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          refreshCartDelivery(document);
          window.NoomaDeliveryI18n?.paint(document);
        });
      });
    } catch (error) {
      window.NoomaCartDrawerUpsellBusy?.clear?.();
      throw error;
    }
  }

  /**
   * Cart POSTs embed Shopify-origin EN section HTML (the Weglot proxy does not translate JSON
   * responses). On translated languages, re-fetch the section over the current — prefixed — URL so
   * the morph paints translated HTML directly (no EN flash, no DOM-wipe repair). If the fetch fails,
   * the embedded EN HTML is morphed as-is (rare, self-heals on the next cart update); EN/original is untouched.
   * @param {string | undefined} embeddedHtml
   * @returns {Promise<string | undefined>}
   */
  async #resolveSectionHtml(embeddedHtml) {
    if (!embeddedHtml || window.NoomaWeglotChrome?.isTranslated?.() !== true) {
      return embeddedHtml;
    }

    // The await below makes rapid cart updates race — a stale response must never morph over a
    // newer one, so anything older than the latest call is dropped (caller skips the morph).
    const fetchId = ++this.#sectionFetchId;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.sectionId);
      const response = await fetch(url.toString());
      if (fetchId !== this.#sectionFetchId) return undefined;
      if (!response.ok) return embeddedHtml;

      const html = await response.text();
      if (fetchId !== this.#sectionFetchId) return undefined;
      return html || embeddedHtml;
    } catch {
      return fetchId === this.#sectionFetchId ? embeddedHtml : undefined;
    }
  }

  /** @type {number} */
  #sectionFetchId = 0;

  /** @type {number} */
  #upsellSyncToken = 0;

  /**
   * Upsell slide identity for the drawer's `slideshow-component`. The slideshow initializes once
   * in `connectedCallback` (scroll/drag, controls, page counter are all sized to the slide set at
   * connect time) — when a hydration morph swaps slides inside the reused element, that state goes
   * stale: dead scroll, wrong page count, swipes falling through to the drawer close gesture.
   * @param {Element | null} drawerInner
   * @returns {string | null}
   */
  #upsellSlideSignature(drawerInner) {
    if (!(drawerInner instanceof HTMLElement)) return null;

    const slideshow = drawerInner.querySelector('.cart-drawer-upsell slideshow-component');
    if (!slideshow) return null;

    const slides = slideshow.querySelectorAll('slideshow-slide');
    return Array.from(slides, (slide) => slide.querySelector('a[href]')?.getAttribute('href') ?? '').join('|');
  }

  /**
   * @param {Element | null} root
   */
  #refreshUpsellPageCounter(root) {
    const slideshow = root?.querySelector?.('.cart-drawer-upsell slideshow-component');
    if (slideshow && typeof slideshow.refreshPageCounter === 'function') {
      slideshow.refreshPageCounter();
    }
  }

  /**
   * Morph replaced upsell forms — re-disable ATC on the new tree if an add is still in flight
   * (do not `clear()` here — product-form `finally` owns unlock so buttons stay disabled while
   * cart settles). Only when caller passes the drawer inner (`this.isDrawer`).
   * When the morph changed the upsell slide set, recreate the slideshow element (clone + replace)
   * so `connectedCallback` re-initializes it for the new slides. When the set is unchanged,
   * refresh the page counter (morph overwrites Liquid slide totals). Scoped to the carousel only —
   * never wipe the whole drawer inner (iOS Safari touch-scroll freeze).
   * @param {Element | null} drawerInner
   * @param {string | null} upsellBefore - slide signature captured before the morph
   */
  #syncUpsellAfterMorph(drawerInner, upsellBefore = null) {
    if (!(drawerInner instanceof HTMLElement)) return;

    const syncToken = ++this.#upsellSyncToken;

    requestAnimationFrame(() => {
      if (syncToken !== this.#upsellSyncToken) return;

      if (!drawerInner.isConnected) {
        window.NoomaCartDrawerUpsellBusy?.clear?.();
        return;
      }

      const slideshow = drawerInner.querySelector('.cart-drawer-upsell slideshow-component');

      if (this.#upsellSlideSignature(drawerInner) !== upsellBefore) {
        if (slideshow instanceof HTMLElement) {
          const fresh = /** @type {HTMLElement} */ (slideshow.cloneNode(true));
          // Runtime state from the old instance must not leak into the fresh init.
          for (const attr of ['in-viewport', 'paused', 'transitioning', 'dragging', 'actioned', 'refreshing-timeline']) {
            fresh.removeAttribute(attr);
          }
          slideshow.replaceWith(fresh);
        }
      }

      // Double rAF: morph just rewrote Liquid counter text; page math needs laid-out slide pitch.
      // Same delay as clone path (async connectedCallback / drawer layout).
      requestAnimationFrame(() => {
        if (syncToken !== this.#upsellSyncToken || !drawerInner.isConnected) return;
        this.#refreshUpsellPageCounter(drawerInner);
        window.NoomaCartDrawerUpsellBusy?.sync?.();
      });
    });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
